import { createFileRoute, Link } from "@tanstack/react-router";
import areas from "@/assets/areas.jpg";
import { Section, SectionHeading, Eyebrow, TrustBar, CtaBand } from "@/components/sections";
import { SERVICE_AREAS, breadcrumbJsonLd } from "@/lib/site";

const areaCopy: Record<(typeof SERVICE_AREAS)[number], string> = {
  Sarasota: "From downtown high-rises to historic neighborhoods like Laurel Park and West of Trail, Anchor Services protects Sarasota homes through every season.",
  Venice: "Island-influenced Venice homes need consistent eyes on humidity, salt and storm impact — exactly what our weekly inspections deliver.",
  Nokomis: "Quiet, water-adjacent Nokomis is a favorite for snowbirds. We keep your home Florida-ready while you're up north.",
  Osprey: "From Bay Street estates to The Oaks, Osprey homes get the same detail-oriented care our Sarasota clients expect.",
  "Lakewood Ranch": "We work across every Lakewood Ranch village, from Country Club to Lake Club, with full knowledge of HOA and gate protocols.",
  "Palmer Ranch": "Prestancia, TPC, Stoneybrook — we know the gates, the cadence and how to keep Palmer Ranch homes pristine year-round.",
  "Siesta Key": "The salt, sand and humidity on Siesta Key are unforgiving on unoccupied homes. Our weekly inspections catch issues before they spread.",
  "Longboat Key": "Whether you're in a tower, a villa or a Bay Isles estate, Anchor Services keeps your Longboat Key residence in show-ready condition.",
  Bradenton: "From Riverwalk to West Bradenton, we serve Manatee County homeowners with the same standards as our Sarasota clients.",
};

export const Route = createFileRoute("/service-areas")({
  head: () => ({
    meta: [
      { title: "Service Areas — Sarasota, Lakewood Ranch, Venice & Beyond | Anchor Services" },
      { name: "description", content: "Anchor Services provides Home Watch and property care across Sarasota, Venice, Nokomis, Osprey, Lakewood Ranch, Palmer Ranch, Siesta Key, Longboat Key and Bradenton." },
      { property: "og:title", content: "Sarasota-Area Home Watch Service Areas" },
      { property: "og:description", content: "Local Home Watch across the Sarasota coast — from Longboat Key to Lakewood Ranch." },
      { property: "og:url", content: "https://anchorsrq.com/service-areas" },
    ],
    links: [{ rel: "canonical", href: "https://anchorsrq.com/service-areas" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd([{ name: 'Home', url: 'https://anchorsrq.com/' }, { name: 'Service Areas', url: 'https://anchorsrq.com/service-areas' }])) },
    ],
  }),
  component: AreasPage,
});

function AreasPage() {
  return (
    <>
      <section className="relative isolate overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 -z-10">
          <img src={areas} alt="" className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.22_0.04_250)]/85 to-[oklch(0.22_0.04_250)]/60" />
        </div>
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-28 lg:px-8">
          <Eyebrow><span className="text-[oklch(0.85_0.12_82)]">Where we work</span></Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl">Trusted across the Sarasota coast.</h1>
          <p className="mt-5 max-w-2xl text-base text-primary-foreground/85 sm:text-lg">
            From the barrier islands to Lakewood Ranch, we serve homeowners across the entire Suncoast.
          </p>
        </div>
      </section>

      <TrustBar />

      <Section>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICE_AREAS.map((a) => (
            <div key={a} className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-xl text-primary">{a}, FL</div>
              <p className="mt-3 text-sm text-muted-foreground">{areaCopy[a]}</p>
              <Link to="/contact" className="mt-4 inline-block text-sm font-medium text-primary underline underline-offset-4">
                Get a quote for {a} →
              </Link>
            </div>
          ))}
        </div>
      </Section>

      <CtaBand title="Don't see your neighborhood?" sub="Reach out — we may already serve nearby and can let you know." />
    </>
  );
}